import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Mail, CheckCircle, Check, Shield, Clock, TrendingUp } from 'lucide-react';
import Button from '../../components/ui/Button';
import AppImage from '../../components/AppImage';
import { createDemoLead } from '../../services/demoLeadService';
import { detectUserCountry, getRegionalPricing } from '../../services/geolocationService';
import { useNavigate } from 'react-router-dom';
import { trackEvent, trackConversion } from '../../hooks/useGoogleAnalytics';
import { initScrollDepthTracking, trackTimeOnPage, trackFormInteraction } from '../../utils/conversionTracking';

const LandingPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [pricing, setPricing] = useState(null);
  const [isLoadingPricing, setIsLoadingPricing] = useState(true);
  const [userCountry, setUserCountry] = useState(null);
  const navigate = useNavigate();

  const isBrazil = userCountry === 'BR';

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const geoData = await detectUserCountry();
        setUserCountry(geoData?.country);
        const regionalPricing = getRegionalPricing(geoData?.country);
        setPricing(regionalPricing);
      } catch (err) {
        console.error('Failed to load pricing:', err);
        const fallbackPricing = getRegionalPricing('US');
        setPricing(fallbackPricing);
      } finally {
        setIsLoadingPricing(false);
      }
    };

    loadPricing();
  }, []);

  // Initialize conversion tracking
  useEffect(() => {
    const cleanupScroll = initScrollDepthTracking();
    const cleanupTime = trackTimeOnPage();

    return () => {
      if (cleanupScroll) cleanupScroll();
      if (cleanupTime) cleanupTime();
    };
  }, []);

  const handleEmailFocus = () => {
    trackFormInteraction('early_access_form', 'email');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!email || !email?.includes('@')) {
      setError(isBrazil ? 'Por favor, insira um email válido' : 'Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await createDemoLead({
        email,
        source: 'early_access_page',
        status: 'new'
      });
      
      trackConversion('early_access_signup', 0);
      trackEvent('lead_capture', {
        source: 'early_access_page',
        page: '/landing'
      });
      
      setIsSuccess(true);
      setEmail('');
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Failed to submit email:', err);
      setError(err?.message || (isBrazil ? 'Falha ao enviar. Tente novamente.' : 'Failed to submit. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bilingual content
  const content = {
    earlyAccessBadge: isBrazil ? 'Acesso Antecipado' : 'Early Access',
    headline: isBrazil 
      ? 'Gestão simples para academias de Jiu-Jitsu. Alunos, presença e pagamentos automatizados.'
      : 'Simple management for Jiu-Jitsu academies. Students, attendance and payments automated.',
    subheadline: isBrazil
      ? 'Criado por praticantes de Jiu-Jitsu. IA integrada avisa quando algo está errado.'
      : 'Built by Jiu-Jitsu practitioners. Integrated AI alerts you when something is wrong.',
    problemTitle: isBrazil ? 'Cansou disso?' : 'Tired of this?',
    problemText: isBrazil
      ? 'Planilhas desorganizadas. WhatsApp para cobrar alunos. Sem visão dos números. Alunos cancelando sem aviso.'
      : 'Messy spreadsheets. WhatsApp for billing. No visibility of numbers. Students canceling without notice.',
    pricingContext: isBrazil
      ? 'Planos custam menos que perder um aluno por mês.' :'Plans cost less than losing one student per month.',
    ctaButton: isBrazil ? 'Garantir Acesso Antecipado' : 'Join Early Access',
    ctaUrgency: isBrazil ? 'Vagas limitadas para academias fundadoras' : 'Limited spots for founding academies',
    trialBenefit: isBrazil ? '60 dias grátis' : '60 days free',
    noFees: isBrazil ? 'Sem taxas ocultas' : 'No hidden fees',
    autoUpgrade: isBrazil ? 'Upgrade automático' : 'Automatic upgrade',
    trustCloud: isBrazil ? 'Sistema em nuvem' : 'Cloud-based system',
    trustSecure: isBrazil ? 'Dados seguros' : 'Secure data',
    trustCancel: isBrazil ? 'Cancele quando quiser' : 'Cancel anytime',
    successMessage: isBrazil
      ? 'Obrigado! Você receberá acesso antecipado em breve.'
      : 'Thank you! You\'ll receive early access soon.',
    emailPlaceholder: isBrazil ? 'Digite seu email' : 'Enter your email',
    submitting: isBrazil ? 'Enviando...' : 'Submitting...',
    contactText: isBrazil ? 'Quer saber mais ou se tornar um parceiro?' : 'Want to know more or become a prospect?',
    upTo: isBrazil ? 'Até' : 'Up to',
    perMonth: isBrazil ? 'mês' : 'month'
  };

  const metaTitle = isBrazil
    ? 'MyDojo - Acesso Antecipado | Software para Academias de Jiu-Jitsu'
    : 'MyDojo - Early Access | Software for Jiu-Jitsu Academies';
  
  const metaDescription = isBrazil
    ? 'Garanta acesso antecipado ao MyDojo. Gestão simples para academias de BJJ com IA integrada. Vagas limitadas para academias fundadoras.'
    : 'Secure early access to MyDojo. Simple management for BJJ academies with integrated AI. Limited spots for founding academies.';

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mydojo.com/landing" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href="https://mydojo.com/landing" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-[#000000] via-[#555555] to-[#000000] flex flex-col items-center justify-center px-4 py-12">
        {/* Logo */}
        <div className="mb-8 animate-fade-in">
          <AppImage
            src="/assets/images/big_logo_icon_white-1767413559816.png"
            alt="MyDojo Logo - Brazilian Jiu-Jitsu Academy Management Platform"
            className="h-32 w-auto md:h-40 object-contain"
          />
        </div>
        {/* Early Access Badge */}
        <div className="mb-6">
          <span className="inline-block bg-[#B22222] text-white px-6 py-2 rounded-full text-sm font-semibold tracking-wider uppercase shadow-lg">
            {content?.earlyAccessBadge}
          </span>
        </div>
        {/* Headline */}
        <h1 className="text-3xl md:text-5xl font-bold text-white text-center mb-4 max-w-4xl leading-tight">
          {content?.headline}
        </h1>
        <p className="text-lg md:text-xl text-[#AAAAAA] text-center mb-8 max-w-2xl">
          {content?.subheadline}
        </p>
        {/* Problem Statement */}
        <div className="w-full max-w-2xl mb-12 bg-[#555555]/20 border border-[#555555] rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-3 text-center">{content?.problemTitle}</h3>
          <p className="text-[#AAAAAA] text-center leading-relaxed">
            {content?.problemText}
          </p>
        </div>
        {/* Pricing Context */}
        {!isLoadingPricing && pricing && (
          <div className="w-full max-w-5xl mb-8">
            <p className="text-lg text-white text-center mb-6 font-medium">
              {content?.pricingContext}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Basic Plan */}
              <div className="bg-[#555555]/20 border-2 border-green-500 rounded-xl p-6 hover:border-green-400 transition-all">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🟢</span>
                  <h3 className="text-xl font-bold text-white">{isBrazil ? 'Básico' : 'Basic'}</h3>
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-white">{pricing?.basic?.formatted}</span>
                  <span className="text-[#AAAAAA] ml-2">/ {content?.perMonth}</span>
                </div>
                <div className="flex items-center gap-2 text-[#AAAAAA] mb-6">
                  <Check className="w-5 h-5 text-green-500" />
                  <span>{content?.upTo} {pricing?.basic?.limit}</span>
                </div>
              </div>

              {/* Pro Plan */}
              <div className="bg-[#555555]/20 border-2 border-yellow-500 rounded-xl p-6 hover:border-yellow-400 transition-all transform md:scale-105">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🟡</span>
                  <h3 className="text-xl font-bold text-white">Pro</h3>
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-white">{pricing?.pro?.formatted}</span>
                  <span className="text-[#AAAAAA] ml-2">/ {content?.perMonth}</span>
                </div>
                <div className="flex items-center gap-2 text-[#AAAAAA] mb-6">
                  <Check className="w-5 h-5 text-yellow-500" />
                  <span>{content?.upTo} {pricing?.pro?.limit}</span>
                </div>
              </div>

              {/* Unlimited Plan */}
              <div className="bg-[#555555]/20 border-2 border-red-500 rounded-xl p-6 hover:border-red-400 transition-all">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-2xl">🔴</span>
                  <h3 className="text-xl font-bold text-white">Unlimited</h3>
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-white">{pricing?.unlimited?.formatted}</span>
                  <span className="text-[#AAAAAA] ml-2">/ {content?.perMonth}</span>
                </div>
                <div className="flex items-center gap-2 text-[#AAAAAA] mb-6">
                  <Check className="w-5 h-5 text-red-500" />
                  <span>{pricing?.unlimited?.limit}</span>
                </div>
              </div>
            </div>

            {/* Pricing Features */}
            <div className="mt-8 flex flex-wrap justify-center gap-6 text-center">
              <div className="flex items-center gap-2 text-[#AAAAAA]">
                <span className="text-xl">🎁</span>
                <span>{content?.trialBenefit}</span>
              </div>
              <div className="flex items-center gap-2 text-[#AAAAAA]">
                <span className="text-xl">🚫</span>
                <span>{content?.noFees}</span>
              </div>
              <div className="flex items-center gap-2 text-[#AAAAAA]">
                <span className="text-xl">📈</span>
                <span>{content?.autoUpgrade}</span>
              </div>
            </div>
          </div>
        )}
        {/* Email Capture Form */}
        <div className="w-full max-w-md mb-8">
          {isSuccess ? (
            <div className="bg-green-500/20 border border-green-500 rounded-lg p-4 flex items-center gap-3 animate-fade-in">
              <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0" />
              <p className="text-green-100 text-sm">
                {content?.successMessage}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#AAAAAA] w-5 h-5" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    placeholder={content?.emailPlaceholder}
                    className="w-full pl-12 pr-4 py-4 bg-[#555555]/30 border border-[#555555] rounded-lg text-white placeholder-[#AAAAAA] focus:outline-none focus:ring-2 focus:ring-[#B22222] focus:border-transparent transition-all"
                    disabled={isSubmitting}
                    onFocus={handleEmailFocus}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-4 bg-[#B22222] hover:bg-[#8B1A1A] text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? content?.submitting : content?.ctaButton}
                </Button>
              </div>
              {error && (
                <p className="text-red-400 text-sm text-center">{error}</p>
              )}
              {/* CTA Urgency */}
              <p className="text-[#AAAAAA] text-sm text-center italic">
                {content?.ctaUrgency}
              </p>
            </form>
          )}
        </div>
        {/* Trust Elements */}
        <div className="w-full max-w-3xl mb-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="flex flex-col items-center gap-2">
              <Shield className="w-6 h-6 text-[#B22222]" />
              <span className="text-[#AAAAAA] text-sm">{content?.trustSecure}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#B22222]" />
              <span className="text-[#AAAAAA] text-sm">{content?.trustCloud}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Clock className="w-6 h-6 text-[#B22222]" />
              <span className="text-[#AAAAAA] text-sm">{content?.trustCancel}</span>
            </div>
          </div>
        </div>
        {/* Contact Email */}
        <div className="text-center">
          <p className="text-[#AAAAAA] text-sm mb-2">{content?.contactText}</p>
          <a
            href="mailto:info@mydojo.info"
            className="text-[#B22222] hover:text-[#8B1A1A] font-medium transition-colors inline-flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            info@mydojo.info
          </a>
        </div>
        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-[#555555] text-xs">
            © {new Date()?.getFullYear()} MyDojo. A product of Sinistro Photo Film & Design Inc.
          </p>
          <p className="text-[#555555] text-xs mt-1">
            All rights reserved.
          </p>
          <div className="mt-4 flex items-center justify-center space-x-4 text-xs">
            <button
              onClick={() => navigate('/terms-of-service')}
              className="text-[#555555] hover:text-[#333333] transition-colors"
            >
              Terms of Service
            </button>
            <span className="text-[#555555]">•</span>
            <button
              onClick={() => navigate('/privacy-policy')}
              className="text-[#555555] hover:text-[#333333] transition-colors"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default LandingPage;